import type { Entries } from './catalog';

/**
 * The copy of an app connecting to a product for the person (OAuth 2.1, 2026-10-22): the sign-in
 * screen's word on where it leads, the consent at `/connect` with the causes it could not ask, and
 * AI apps with access (`/connections`). The app's name is always its own claim, so the copy never vouches
 * for it.
 *
 * Each area keeps its own file so its keys travel with its screens; a key here must not repeat one of
 * `en.ts`, `es.ts` or another area.
 */
export const connect: Record<'en' | 'es', Entries> = {
	en: {
		'signin.connection': 'Sign in to review an app’s request to use {product}.',
		'signin.connection.unnamed': 'Sign in to review an app’s request to use a Beyond product.',
		'nav.connections': 'AI apps with access',

		'connect.reading': 'Reading the app’s request…',
		'connect.title': 'Choose what {client} can do',
		'connect.lead': 'Allow it only if you just started connecting it.',
		'connect.unverified': 'Unverified',
		'connect.unverified.detail': 'The app chose this name. Accounts has not verified it.',
		'connect.host': 'Answers at',
		'connect.product': 'Product',
		'connect.person.unnamed': 'Your Beyond account',
		'connect.local': 'This app runs on this computer',
		'connect.local.detail': 'It receives your access at {host}. Allow it only if you just started connecting it here.',
		'connect.organization': 'Where it can act',
		'connect.organization.all': 'All your organizations',
		'connect.organization.hint': 'All your organizations includes the ones you join later.',
		'connect.reauthenticate': 'You will confirm it is you before it is allowed.',
		'connect.allow': 'Allow',
		'connect.allowing': 'Allowing…',
		'connect.cancel': 'Cancel',
		'connect.later': 'You can remove its access at any time in AI apps with access.',
		'connect.reauth': 'Confirm it is you to allow it. If you cannot here, sign in again, then connect again from the app.',
		'connect.gone': 'This request is no longer valid. Start connecting again from the app.',
		'connect.onward': 'Go to your overview',
		'connect.framed': 'This request can only be answered in a window of its own. Open the address in your browser.',

		'connect.problem.title': 'This app could not connect',
		'connect.problem.client': 'Accounts does not know this app, or does not read its description from that address.',
		'connect.problem.document': 'The app’s description is not valid, so Accounts cannot show what it asks.',
		'connect.problem.unreachable': 'Accounts could not read the app’s description in time.',
		'connect.problem.redirect': 'The app asked to answer at an address it did not register.',
		'connect.problem.unavailable': 'Accounts could not answer, so nothing was connected.',
		'connect.problem.other': 'The request could not be read, so nothing was connected.',
		'connect.problem.again': 'Start connecting again from the app.',
		'connect.problem.developer': 'If it happens again, tell the app’s developer.',
		'connect.problem.later': 'Try again from the app in a moment.',

		'connections.lead': 'Apps such as Claude, ChatGPT or Codex that you connected through MCP. They use Beyond products for you, with the permissions you gave them.',
		'connections.none': 'No AI app has access',
		'connections.none.detail': 'An app appears here once you allow it to use a Beyond product for you.',
		'connections.organization.left': 'An organization you no longer belong to',
		'connections.created': 'access since {date}',
		'connections.used': 'last used {date}',
		'connections.scopes': 'What it can do',
		'connections.connected': 'Access since',
		'connections.last': 'Last used',
		'connections.never': 'Not used yet',
		'connections.ends': 'Expires',
		'connections.disconnect': 'Remove access',
		'connections.disconnect.item': 'Remove access…',
		'connections.disconnect.title': 'Remove {client}’s access?',
		'connections.disconnect.body': '{client} stops using {product} for you within a minute. Work it already started keeps running. To give it access again, connect it again from the app.',
		'connections.disconnect.accept': 'Remove access',
		'connections.disconnected': '{client}’s access was removed.'
	},
	es: {
		'signin.connection': 'Inicia sesión para revisar la solicitud de una app para usar {product}.',
		'signin.connection.unnamed': 'Inicia sesión para revisar la solicitud de una app para usar un producto de Beyond.',
		'nav.connections': 'Apps de IA con acceso',

		'connect.reading': 'Leyendo la solicitud de la app…',
		'connect.title': 'Elige qué puede hacer {client}',
		'connect.lead': 'Permítelo solo si acabas de empezar a conectarla.',
		'connect.unverified': 'Sin verificar',
		'connect.unverified.detail': 'La app eligió este nombre. Accounts no lo verificó.',
		'connect.host': 'Responde en',
		'connect.product': 'Producto',
		'connect.person.unnamed': 'Tu cuenta de Beyond',
		'connect.local': 'Esta app se ejecuta en este equipo',
		'connect.local.detail': 'Recibe tu acceso en {host}. Permítelo solo si acabas de empezar a conectarla aquí.',
		'connect.organization': 'Dónde puede actuar',
		'connect.organization.all': 'Todas tus organizaciones',
		'connect.organization.hint': 'Todas tus organizaciones incluye aquellas a las que te unas más adelante.',
		'connect.reauthenticate': 'Confirmarás que eres tú antes de permitirlo.',
		'connect.allow': 'Permitir',
		'connect.allowing': 'Permitiendo…',
		'connect.cancel': 'Cancelar',
		'connect.later': 'Puedes quitarle el acceso cuando quieras en Apps de IA con acceso.',
		'connect.reauth': 'Confirma que eres tú para permitirlo. Si no puedes aquí, vuelve a iniciar sesión y conéctala de nuevo desde la app.',
		'connect.gone': 'Esta solicitud ya no es válida. Empieza a conectarla de nuevo desde la app.',
		'connect.onward': 'Ir a tu resumen',
		'connect.framed': 'Esta solicitud solo se puede responder en una ventana propia. Abre la dirección en tu navegador.',

		'connect.problem.title': 'Esta app no se pudo conectar',
		'connect.problem.client': 'Accounts no conoce esta app o no lee su descripción desde esa dirección.',
		'connect.problem.document': 'La descripción de la app no es válida, así que Accounts no puede mostrar lo que pide.',
		'connect.problem.unreachable': 'Accounts no pudo leer a tiempo la descripción de la app.',
		'connect.problem.redirect': 'La app pidió responder en una dirección que no registró.',
		'connect.problem.unavailable': 'Accounts no pudo responder, así que no se conectó nada.',
		'connect.problem.other': 'No se pudo leer la solicitud, así que no se conectó nada.',
		'connect.problem.again': 'Empieza a conectarla de nuevo desde la app.',
		'connect.problem.developer': 'Si vuelve a pasar, avisa a quien desarrolla la app.',
		'connect.problem.later': 'Vuelve a intentarlo desde la app en un momento.',

		'connections.lead': 'Apps como Claude, ChatGPT o Codex que conectaste por MCP. Usan productos de Beyond por ti, con los permisos que les diste.',
		'connections.none': 'Ninguna app de IA tiene acceso',
		'connections.none.detail': 'Una app aparece aquí cuando le permites usar un producto de Beyond por ti.',
		'connections.organization.left': 'Una organización a la que ya no perteneces',
		'connections.created': 'con acceso desde el {date}',
		'connections.used': 'usada por última vez el {date}',
		'connections.scopes': 'Lo que puede hacer',
		'connections.connected': 'Acceso desde',
		'connections.last': 'Usada por última vez',
		'connections.never': 'Todavía no se usó',
		'connections.ends': 'Vence',
		'connections.disconnect': 'Quitar acceso',
		'connections.disconnect.item': 'Quitar acceso…',
		'connections.disconnect.title': '¿Quitar el acceso de {client}?',
		'connections.disconnect.body': '{client} deja de usar {product} por ti en menos de un minuto. El trabajo que ya empezó sigue en marcha. Para devolverle el acceso, vuelve a conectarla desde la app.',
		'connections.disconnect.accept': 'Quitar acceso',
		'connections.disconnected': 'Se quitó el acceso de {client}.'
	}
};
